import { Button } from "@stackje/shared-components/ui/button";
import { Download, Share } from "lucide-react";
import { useEffect, useState } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

interface InstallPwaFullPageProps {
  children: React.ReactNode;
}

type DeviceType = "iOS" | "Android" | "desktop";

// Define the standalone property type
interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

export function InstallPwaFullPage({ children }: InstallPwaFullPageProps) {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [deviceType, setDeviceType] = useState<DeviceType>("desktop");

  useEffect(() => {
    // Detect device type
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /ipad|iphone|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid;

    // Only proceed if it's a mobile device
    if (!isMobile) {
      setDeviceType("desktop");
      return;
    }

    setDeviceType(isIOS ? "iOS" : "Android");

    // Handle installation prompt
    if (isAndroid) {
      const handler = (e: Event) => {
        e.preventDefault();
        setDeferredPrompt(e as BeforeInstallPromptEvent);
        setShowPrompt(true);
      };

      window.addEventListener("beforeinstallprompt", handler);

      return () => {
        window.removeEventListener("beforeinstallprompt", handler);
      };
    } else if (isIOS) {
      const isStandalone =
        window.matchMedia("(display-mode: standalone)").matches ||
        (navigator as NavigatorWithStandalone).standalone;
      if (!isStandalone) {
        setShowPrompt(true);
      }
    }
  }, []);

  // If it's desktop, don't show the prompt at all
  if (deviceType === "desktop") {
    return <>{children}</>;
  }

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setShowPrompt(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
  };

  if (!showPrompt) {
    return <>{children}</>;
  }

  return (
    <div className="w-dvw h-dvh overflow-auto bg-gradient-to-br from-red-900 via-orange-800 to-orange-700 flex items-center justify-center p-4 text-white pb-safe pt-safe pl-safe pr-safe">
      <div className="max-w-md w-full space-y-8 text-center">
        <p className="text-6xl">📲</p>
        <h2 className="text-3xl font-bold">Install Stack.je as an App</h2>

        {deviceType === "iOS" ? (
          <>
            <p className="text-lg text-orange-200">
              Experience the ease of a mobile app — add this to your home screen
              to manage your sites seamlessly on the go.
            </p>
            <div className="bg-white/10 backdrop-blur-md rounded-lg p-4 mt-4">
              <ol className="text-left space-y-4 text-lg text-orange-200">
                <li className="flex items-center gap-2">
                  1. Tap the <Share className="h-5 w-5" /> Share button
                </li>
                <li>2. Scroll down and tap &ldquo;Add to Home Screen&rdquo;</li>
                <li>3. Tap &ldquo;Add&rdquo; in the top right corner</li>
              </ol>
            </div>
            <div className="space-y-2 mt-4">
              <Button
                onClick={handleDismiss}
                variant="outline"
                size="lg"
                className="w-full text-lg bg-white/10 border-orange-400 text-orange-200 hover:bg-white/20"
              >
                I&apos;ll do it later
              </Button>
            </div>
          </>
        ) : (
          <>
            <p className="text-lg text-orange-200">
              Experience the ease of a mobile app — add this to your home screen
              to manage your sites seamlessly on the go.
            </p>

            <div className="space-y-4 mt-6 py-2">
              <Button
                onClick={handleInstallClick}
                size="lg"
                className="w-full text-lg bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700"
              >
                <Download className="mr-2 h-5 w-5" />
                Add to Home Screen
              </Button>
              <Button
                onClick={handleDismiss}
                variant="outline"
                size="lg"
                className="w-full text-lg bg-white/10 border-orange-400 text-orange-200 hover:bg-white/20"
              >
                Continue to Website
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
